/* __next_internal_client_entry_do_not_use__ ShareButton auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { IconButton } from "@faire/web--source/slate/IconButton";
import { Share } from "@faire/web--source/slate/icons/foundation/Share";
import { Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { trackRetailerTeamManagementShareButtonClick } from "@faire/web-api--source/events/retailerTeamManagement/click/shareButton";
import { trackRetailerTeamManagementShareButtonView } from "@faire/web-api--source/events/retailerTeamManagement/view/shareButton";
import { MULTI_USER_GTM_PDP_AND_BRAND_PAGE_V2_SILENCING } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/MULTI_USER_GTM_PDP_AND_BRAND_PAGE_V2_SILENCING";
import { ShareableItemType } from "@faire/web-api--source/indigofair/retailer/ShareableItemType";
import React from "react";
import styled, { css } from "styled-components";
import { getSettingMultiUserGTMV2NoConstraintsAndAssign, getSettingMultiUserGTMV2NoConstraints } from "@faire/retailer-visitor-shared/settings/getSettingMultiUserGTMV2";
import { useRetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { LeanInviteTeamModal } from "@faire/retailer-visitor-shared/views/multi-user/Invite/LeanInviteTeamModal";
import { VIEW_COUNT_BEFORE_SILENCING_GTM } from "@faire/retailer-visitor-shared/views/multi-user/Share/consts";
import { ShareWithTeamModal } from "@faire/retailer-visitor-shared/views/multi-user/Share/ShareWithTeamModal";
import { ShareWithTeamGTMModal } from "@faire/retailer-visitor-shared/views/multi-user/Share/ShareWithTeamGTMModal";
var ModalType;
(function(ModalType) {
    ModalType["NONE"] = "NONE";
    ModalType["SHARE_MODAL"] = "SHARE_MODAL";
    ModalType["LEAN_INVITE_MODAL"] = "LEAN_INVITE_MODAL";
    ModalType["SHARE_MODAL_WITH_GTM"] = "SHARE_MODAL_WITH_GTM";
})(ModalType || (ModalType = {}));
export var ShareButton = function(param) {
    var type = param.type, image = param.image, name = param.name, subText = param.subText, token = param.token, variant = param.variant, pageName = param.pageName, buttonText = param.buttonText, isIconButton = param.isIconButton, iconSize = param.iconSize;
    var _React_useState = _sliced_to_array(React.useState(ModalType.NONE), 2), currentModal = _React_useState[0], setCurrentModal = _React_useState[1];
    var hasEverHadActiveSubUser = useRetailerStore([
        "hasEverHadActiveSubUser"
    ]).hasEverHadActiveSubUser;
    var ref = useDoActionWhenVisible(function() {
        trackRetailerTeamManagementShareButtonView(pageName);
    });
    var handleShareButtonClick = function() {
        if (!hasEverHadActiveSubUser && getSettingMultiUserGTMV2NoConstraints()) {
            setCurrentModal(ModalType.SHARE_MODAL_WITH_GTM);
        } else {
            setCurrentModal(ModalType.SHARE_MODAL);
        }
        trackRetailerTeamManagementShareButtonClick(pageName);
    };
    if (!hasEverHadActiveSubUser && !getSettingMultiUserGTMV2NoConstraintsAndAssign()) {
        return null;
    }
    var showCopyLinkButton = type === ShareableItemType.PRODUCT || type === ShareableItemType.BRAND;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            currentModal === ModalType.SHARE_MODAL_WITH_GTM ? /*#__PURE__*/ _jsx(ShareWithTeamGTMModal, {
                modalType: type,
                image: image,
                name: name,
                subText: subText,
                onClose: function() {
                    return setCurrentModal(ModalType.NONE);
                },
                onInvite: function() {
                    return setCurrentModal(ModalType.NONE);
                },
                pageName: pageName,
                silenceGTMMilestone: MULTI_USER_GTM_PDP_AND_BRAND_PAGE_V2_SILENCING,
                viewCountBeforeSilencingGTM: VIEW_COUNT_BEFORE_SILENCING_GTM
            }) : null,
            currentModal === ModalType.SHARE_MODAL ? /*#__PURE__*/ _jsx(ShareWithTeamModal, {
                modalType: type,
                image: image,
                name: name,
                subText: subText,
                token: token,
                onClose: function() {
                    return setCurrentModal(ModalType.NONE);
                },
                onInviteButtonClick: function() {
                    return setCurrentModal(ModalType.LEAN_INVITE_MODAL);
                },
                pageName: pageName,
                showCopyLinkButton: showCopyLinkButton
            }) : null,
            currentModal === ModalType.LEAN_INVITE_MODAL ? /*#__PURE__*/ _jsx(LeanInviteTeamModal, {
                onBack: function() {
                    return setCurrentModal(ModalType.SHARE_MODAL);
                },
                onClose: function() {
                    return setCurrentModal(ModalType.NONE);
                },
                onInvite: function() {
                    return setCurrentModal(ModalType.NONE);
                },
                pageName: pageName
            }) : null,
            /*#__PURE__*/ _jsx(Row, {
                ref: ref,
                children: /*#__PURE__*/ _jsx(StyledShareButton, {
                    buttonText: buttonText,
                    variant: variant,
                    isIconButton: isIconButton,
                    handleShareButtonClick: handleShareButtonClick,
                    iconSize: iconSize
                })
            })
        ]
    });
};
var StyledShareButton = function(param) {
    var buttonText = param.buttonText, variant = param.variant, isIconButton = param.isIconButton, handleShareButtonClick = param.handleShareButtonClick, iconSize = param.iconSize;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var label = strictLocalize({
        id: "iVSuEm",
        defaultMessage: [
            {
                type: 0,
                value: "Share"
            }
        ]
    });
    if (buttonText) {
        return /*#__PURE__*/ _jsxs(StyledButtonWithText, {
            onClick: handleShareButtonClick,
            variant: variant,
            "aria-label": label,
            "data-test-id": "share-icon-wrapper",
            $hasMinWidth: variant !== "tertiary",
            children: [
                /*#__PURE__*/ _jsx(StyledShareIcon, {
                    fontSize: iconSize !== null && iconSize !== void 0 ? iconSize : "22px"
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    width: "1x"
                }),
                buttonText
            ]
        });
    }
    if (isIconButton) {
        return /*#__PURE__*/ _jsx(IconButton, {
            onClick: handleShareButtonClick,
            variant: variant,
            "aria-label": label,
            "data-test-id": "share-icon-wrapper",
            children: /*#__PURE__*/ _jsx(StyledShareIcon, {
                fontSize: "22px"
            })
        });
    }
    return /*#__PURE__*/ _jsx(StyledButton, {
        onClick: handleShareButtonClick,
        variant: variant,
        "aria-label": label,
        "data-test-id": "share-icon-wrapper",
        $fixedWidth: variant === "secondary" && !buttonText,
        children: /*#__PURE__*/ _jsx(StyledShareIcon, {
            fontSize: "22px"
        })
    });
};
var StyledShareIcon = styled(Share).withConfig({
    componentId: "sc-c0451c8d-0"
})([
    "&:hover{color:",
    ";}"
], Core.icon.hover);
var StyledButton = styled(Button).withConfig({
    componentId: "sc-c0451c8d-1"
})([
    "",
    ""
], function(param) {
    var $fixedWidth = param.$fixedWidth;
    return $fixedWidth && css([
        "width:48px;",
        "{width:40px;height:40px;padding:0;}"
    ], responsiveMediaQueries.mobile);
});
var StyledButtonWithText = styled(Button).withConfig({
    componentId: "sc-c0451c8d-2"
})([
    "",
    " ",
    "{width:100%;}"
], function(param) {
    var $hasMinWidth = param.$hasMinWidth;
    return $hasMinWidth && css([
        "min-width:180px;"
    ]);
}, responsiveMediaQueries.mobile);
